// feedbackMeta.ts — presentation metadata for the feedback board.
// Pure data + pure functions, no DOM. Shared by the public board, the Owner
// tracker and the Council view so all three label things identically.

import type { IconName } from '@/icons';
import { ApiError } from '@/core/lib/apiClient';
import type { FeedbackCategory, FeedbackStatus } from '@/core/models/feedback';

/** Mirrors backend DEFAULT_LIST_LIMIT/MAX_LIST_LIMIT (api.py) — the list
 * endpoint never returns more than this many rows for one request. Used to
 * render a tab count as "100+" instead of a flat "100" when the underlying
 * read hit the cap, so it can never quietly contradict the Total stat tile
 * (2026-08-10 adversarial review, item 7). */
export const FEEDBACK_LIST_CAP = 100;

/** "42" below the cap, "100+" at/above it. */
export function formatListCount(count: number): string {
  return count >= FEEDBACK_LIST_CAP ? `${FEEDBACK_LIST_CAP}+` : String(count);
}

export const CATEGORY_ICONS: Record<FeedbackCategory, IconName> = {
  bug: 'wrench',
  feature: 'sparkles',
  performance: 'gauge',
  ux: 'palette',
  accessibility: 'accessibility',
  moderation: 'shield',
  other: 'info',
};

/**
 * Per-category chip tone for the 1.21.50 submit panel (spec: "Bug red,
 * Feature request purple, Performance orange, UI/UX blue, others neutral").
 * Hardcoded hex rather than reused theme accent tokens: the theme's
 * accentCyan/accentAmber slots were both re-themed to the same premium-purple
 * hue (see theme/tokens.ts), so there is no orange or blue semantic slot left
 * to borrow — these are deliberately standalone, chosen to hold reasonable
 * contrast as a bordered/tinted chip (mirrors the `${tone}22`-background +
 * solid-tone-text + solid-tone-border pattern already used for the plain
 * topic chip elsewhere in this feature).
 */
export const CATEGORY_TONE_COLOR: Record<FeedbackCategory, string> = {
  bug: '#ff5d6c',
  feature: '#a855f7',
  performance: '#f97316',
  ux: '#3b82f6',
  accessibility: '#94a3b8',
  moderation: '#94a3b8',
  other: '#94a3b8',
};

/**
 * Status → theme token key. Deliberately NOT the moderation tones: a feedback
 * status must never read like a case outcome.
 */
export const STATUS_TONES: Record<FeedbackStatus, 'neutral' | 'info' | 'accent' | 'good' | 'muted'> = {
  'open': 'neutral',
  'under-review': 'info',
  'planned': 'accent',
  'in-progress': 'accent',
  'testing': 'accent',
  'shipped': 'good',
  'resolved': 'good',
  'closed': 'muted',
  'declined': 'muted',
};

/** Statuses that mean "no further work planned" — used to de-emphasise rows. */
export function isClosedStatus(status: FeedbackStatus): boolean {
  return status === 'shipped' || status === 'resolved' || status === 'closed' || status === 'declined';
}

/**
 * Per-topic label + placeholder for the required `body` narrative field.
 * Leading feature-request with "what problem does it solve" rather than
 * "what feature" is deliberate (see slice-2-medium spec) — it yields
 * requests that can be evaluated instead of a pile of prescribed
 * implementations.
 */
export interface FeedbackBodyMeta {
  label: string;
  placeholder: string;
}

export const BODY_META: Record<FeedbackCategory, FeedbackBodyMeta> = {
  bug: { label: 'What happened?', placeholder: 'Describe what happened.' },
  feature: {
    label: 'What problem does it solve?',
    placeholder: "Describe the problem, not the feature you'd build to fix it.",
  },
  performance: { label: 'What felt slow?', placeholder: 'Describe what felt slow, and where.' },
  ux: { label: 'What was confusing?', placeholder: 'Describe what was confusing.' },
  accessibility: { label: 'What is blocked?', placeholder: 'Describe what you could not do.' },
  moderation: {
    label: 'What is wrong with it?',
    placeholder: 'Describe what is wrong with the rule or decision.',
  },
  other: { label: 'Details', placeholder: 'Tell us more.' },
};

// ── Per-topic structured fields ──────────────────────────────────────────────
//
// Mirrors backend/apps/feedback/field_schema.py FEEDBACK_FIELD_SCHEMA — field
// ids, max lengths, and staff-only flags must match, since the server is the
// real validator. This is the pre-1.21.50 per-category `details` JSON schema
// — kept for backward compatibility (existing items still carry these
// answers, and FeedbackDetailPage still renders them read-only). The
// SubmitFeedbackPanel (1.21.50) submit form no longer collects these; it
// collects the newer structured columns instead (see
// backend/apps/feedback/structured_fields.py + this file's
// STRUCTURED_FIELD_LABELS-adjacent constants in SubmitFeedbackPanel.tsx).
//
// The always-required narrative per topic ("what happened", "what felt
// slow", …) is NOT here — that's the existing `body` field, not a JSON key.
// The moderation topic deliberately has no "which user/post" field: see the
// backend field_schema.py docstring for why.

export type FeedbackFieldKind = 'text' | 'textarea' | 'radio';

export interface FeedbackFieldOption {
  value: string;
  label: string;
}

export interface FeedbackFieldDef {
  id: string;
  label: string;
  kind: FeedbackFieldKind;
  maxLength?: number;
  /** Never sent to / rendered for a non-staff viewer. */
  staffOnly?: boolean;
  /**
   * Frontend-enforced only — the backend accepts an empty/absent value here
   * same as any other optional field (see field_schema.py). Submit is
   * disabled while a required field is blank.
   */
  required?: boolean;
  options?: FeedbackFieldOption[];
}

const FREQUENCY_OPTIONS: FeedbackFieldOption[] = [
  { value: 'always', label: 'Always' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'once', label: 'Once' },
];

// Mirrors backend/apps/feedback/field_schema.py exactly — ids, max lengths and
// staff_only flags below must match theirs field-for-field. `additional_context`
// is repeated per-category (not a single top-level key) because the backend
// schema itself repeats it per category — an "Add more details" catch-all box
// available everywhere except `other`, which carries no structured fields at all.
export const FEEDBACK_TOPIC_FIELDS: Record<FeedbackCategory, FeedbackFieldDef[]> = {
  bug: [
    { id: 'expected', label: 'Expected behavior', kind: 'text', maxLength: 500 },
    { id: 'actual', label: 'Actual behavior', kind: 'text', maxLength: 500 },
    { id: 'steps', label: 'Steps to reproduce', kind: 'textarea', maxLength: 2000 },
    {
      id: 'possible_cause',
      label: 'Any idea what causes it? (optional)',
      kind: 'textarea',
      maxLength: 1000,
    },
    { id: 'additional_context', label: 'Additional context', kind: 'textarea', maxLength: 1000 },
    { id: 'frequency', label: 'How often does it happen?', kind: 'radio', options: FREQUENCY_OPTIONS },
    { id: 'where_url', label: 'Where (page/URL)', kind: 'text', maxLength: 500, staffOnly: true },
    {
      id: 'device_browser',
      label: 'Device & browser',
      kind: 'text',
      maxLength: 300,
      staffOnly: true,
    },
    {
      id: 'device_platform',
      label: 'Platform',
      kind: 'radio',
      // Inlined (not a shared const) — the mobile field-schema sync test
      // extracts and evals just the FEEDBACK_TOPIC_FIELDS object literal from
      // this source file, injecting only FREQUENCY_OPTIONS into scope; an
      // identifier reference to any other module-level const here would
      // throw a ReferenceError in that eval (2026-08-10 fix).
      options: [
        { value: 'web', label: 'Web' },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
      ],
      staffOnly: true,
    },
    { id: 'os_version', label: 'OS version', kind: 'text', maxLength: 100, staffOnly: true },
  ],
  feature: [
    { id: 'proposed_solution', label: 'Proposed solution', kind: 'textarea', maxLength: 2000 },
    { id: 'who_it_helps', label: 'Who does this help?', kind: 'text', maxLength: 500 },
    {
      id: 'workaround_today',
      label: 'What do you do today instead?',
      kind: 'textarea',
      maxLength: 1000,
    },
    { id: 'additional_context', label: 'Additional context', kind: 'textarea', maxLength: 1000 },
  ],
  performance: [
    { id: 'where', label: 'Where did it feel slow?', kind: 'text', maxLength: 500 },
    { id: 'when_it_happens', label: 'When does it happen?', kind: 'text', maxLength: 500 },
    { id: 'additional_context', label: 'Additional context', kind: 'textarea', maxLength: 1000 },
    { id: 'frequency', label: 'How often does it happen?', kind: 'radio', options: FREQUENCY_OPTIONS },
    {
      id: 'connection_device',
      label: 'Connection & device',
      kind: 'text',
      maxLength: 300,
      staffOnly: true,
    },
    {
      id: 'device_platform',
      label: 'Platform',
      kind: 'radio',
      // Inlined (not a shared const) — the mobile field-schema sync test
      // extracts and evals just the FEEDBACK_TOPIC_FIELDS object literal from
      // this source file, injecting only FREQUENCY_OPTIONS into scope; an
      // identifier reference to any other module-level const here would
      // throw a ReferenceError in that eval (2026-08-10 fix).
      options: [
        { value: 'web', label: 'Web' },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
      ],
      staffOnly: true,
    },
  ],
  ux: [
    { id: 'which_screen', label: 'Which screen?', kind: 'text', maxLength: 300 },
    // NOT 'expected' — bug already owns that id at a different max length;
    // a distinct id keeps a stray UX answer from surviving a topic switch
    // and being validated/labelled as bug's.
    { id: 'expected_instead', label: 'What did you expect?', kind: 'textarea', maxLength: 1000 },
    { id: 'additional_context', label: 'Additional context', kind: 'textarea', maxLength: 1000 },
  ],
  accessibility: [
    { id: 'assistive_tech', label: 'Assistive technology used', kind: 'text', maxLength: 300 },
    { id: 'what_would_fix', label: 'What would fix it?', kind: 'textarea', maxLength: 1000 },
    { id: 'additional_context', label: 'Additional context', kind: 'textarea', maxLength: 1000 },
  ],
  moderation: [
    {
      id: 'which_rule',
      label: 'Which rule or decision?',
      kind: 'text',
      maxLength: 500,
      // The spec marks BOTH moderation narratives required: body carries
      // "what is wrong with it" (BODY_META.moderation), and this one.
      required: true,
    },
    {
      id: 'what_would_change',
      label: 'What would you change?',
      kind: 'textarea',
      maxLength: 2000,
    },
  ],
  other: [],
};

/**
 * Looks up a field's label within a topic — used to render stored answers
 * back. `?? []` guards an unknown/future category: render nothing rather
 * than throw.
 */
export function fieldLabel(category: FeedbackCategory, fieldId: string): string {
  return (FEEDBACK_TOPIC_FIELDS[category] ?? []).find((f) => f.id === fieldId)?.label ?? fieldId;
}

/** Whether a stored answer's field is staff-only within its topic. */
export function isFieldStaffOnly(category: FeedbackCategory, fieldId: string): boolean {
  return !!(FEEDBACK_TOPIC_FIELDS[category] ?? []).find((f) => f.id === fieldId)?.staffOnly;
}

/**
 * Renders a stored answer's value for display: a radio field's stored value
 * is its raw option value (e.g. "sometimes"), so this resolves it to the
 * option's label ("Sometimes") rather than showing the enum verbatim. Every
 * other field kind stores exactly what should render.
 */
export function fieldValueLabel(category: FeedbackCategory, fieldId: string, value: string): string {
  const field = (FEEDBACK_TOPIC_FIELDS[category] ?? []).find((f) => f.id === fieldId);
  if (field?.kind === 'radio') {
    return field.options?.find((o) => o.value === value)?.label ?? value;
  }
  return value;
}

export interface FeedbackFieldError {
  fieldId: string;
  /** Names the field and its problem, e.g. `"Where (page/URL)" is too long.` */
  message: string;
}

/**
 * Turns a 400's ApiError.fields (field id -> "too_long" | "invalid") into a
 * message naming the offending field and its problem, for a field that
 * belongs to this topic's structured-detail schema. Returns null when the
 * error carries nothing that maps onto a known detail field (e.g. a plain
 * title/body/category validation error) — the caller should fall back to a
 * generic message in that case.
 */
export function describeFeedbackFieldError(
  category: FeedbackCategory,
  err: Pick<ApiError, 'fields'>,
): FeedbackFieldError | null {
  const fields = err.fields;
  if (!fields) return null;
  for (const f of FEEDBACK_TOPIC_FIELDS[category] ?? []) {
    const code = fields[f.id];
    if (typeof code !== 'string') continue;
    const problem = code === 'too_long' ? 'is too long' : "isn't valid";
    return { fieldId: f.id, message: `"${f.label}" ${problem}.` };
  }
  return null;
}

/** Short relative time ("3d", "just now") for list rows. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

/** Human-facing "#FB-2026-00124" style rendering of a legacy id like "fb-124".
 * Purely cosmetic — the real id sent to every endpoint is the raw `item.id`. */
export function displayFeedbackId(id: string, createdAt: string): string {
  const num = id.match(/(\d+)$/)?.[1] ?? id;
  const year = new Date(createdAt).getFullYear();
  const yearPart = Number.isNaN(year) ? '' : `${year}-`;
  return `#FB-${yearPart}${num.padStart(5, '0')}`;
}

/** Accepts either the raw id form ("fb-42") or the displayed form
 * ("#FB-2026-00042", any-case, zero-padded, year segment optional) and
 * normalizes both to the raw id the API expects. Falls through to the
 * trimmed input unchanged when it matches neither shape (e.g. a future
 * non-numeric legacy id) — the server is still the real validator. */
export function parseFeedbackIdInput(raw: string): string {
  const trimmed = raw.trim();
  const m = /^#?FB-(?:\d{4}-)?0*(\d+)$/i.exec(trimmed);
  return m ? `fb-${m[1]}` : trimmed;
}

/** Shared describer for a can_manage-gated mutation's failure — every manage
 * surface (status/priority/assign/pin/roadmap/stages/relations) routes
 * through this so a 403 (the server revoked/never granted edit access, e.g.
 * an assignment changed under you) reads as an honest, specific message
 * instead of a generic "something went wrong". Mirrors
 * attachmentUploadErrorMessage's shape. Note: the backend's PermissionDenied
 * body carries no distinguishing `code` for "cannot_manage" vs "staff_only"
 * (both pass through DRF's default handler as `{"detail": ...}`, not the
 * app's `{code}` convention) — so this keys off `status === 403` alone,
 * which is exactly what every one of these endpoints uses for an edit-rights
 * failure. */
export function describeManageError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You no longer have edit access to this feedback.';
    if (err.status === 404) return 'This feedback item could not be found.';
  }
  return fallback;
}

/** Human file size ("340 KB", "2.1 MB") for the attachment preview cards.
 * Bytes below 1000 render as "N B" rather than a fractional KB. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1000) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

/** Curated tag label constraints — mirrors backend api.py TAG_MAX_LEN /
 * MAX_TAGS_PER_ITEM exactly (see FeedbackItem.tags' docstring for why this
 * is a validated JSON list, not a separate model). */
export const TAG_MAX_LEN = 30;
export const MAX_TAGS_PER_ITEM = 6;

/** Maps a feedback attachment upload failure to user-facing copy — mirrors
 * mediaService.uploadErrorMessage's shape for the same honesty guarantee. */
export function attachmentUploadErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.code === 'file_too_large') return 'File too large (max 15 MB).';
    if (e.code === 'empty_file') return 'That file is empty.';
    if (e.code === 'unsupported_format')
      return 'Unsupported file type (use PNG, JPG, WebP, GIF, MP4, PDF or TXT).';
    if (e.code === 'too_many_attachments') return 'This item already has the maximum of 5 attachments.';
    if (e.code === 'file_required') return 'Choose a file first.';
  }
  return fallback;
}
